import React, { SetStateAction, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import Step1 from './Step1';
import Step2 from './Step2';
import Step4 from './Step4';
import { StepProps } from '../../../pages/complete-profile';
import { initialValues, activityLevelOptions, overallHealthOptions, exerciseFrequencyOptions, smokingStatusOptions, alcoholFrequencyOptions, workingStyleOptions, dietTypeOptions, eatingPatternOptions, hydrationOptions, exerciseRoutineOptions, calmingActivityOptions, normalizeSupplementsFromApi, medicalImplantOptions, practicalConsiderationOptions, isScanOrPlusMembership, getOnboardingTotalSteps, shouldShowFemaleSexHormonesStep } from '../../../types/membersTypes';
import { formatInTimeZone } from 'date-fns-tz';
import MedicalHistoryStep from './MedicalHistoryStep';
import MRISafetyStep from './MRISafetyStep';
import DeclarationStep from './DeclarationStep';
import Step5 from './Step5';
import axios from 'axios';
import { isProd, races, genderDisplayOptions, weightMeasurementTypes } from '../../../utils/constants';
import { categorizeError, ErrorInfo } from '../../../utils/errorHandler';
import { getProductType } from '../../../utils/dataLayer';
import { shouldShowIncompleteStepReminder, getOnboardingResumeStep } from '../../../utils/onboardingStepCompletion';

interface IProps {
  currentStep: StepProps;
  setCurrentStep: (step: StepProps) => void;
  setIsSubmitted: React.Dispatch<SetStateAction<boolean>>;
  onError?: (errorInfo: ErrorInfo) => void;
}

const CompleteProfileForm: React.FC<IProps> = ({
  currentStep,
  setCurrentStep,
  setIsSubmitted,
  onError
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [, setIsFormError] = React.useState<boolean>(false);
  const [memberData, setMemberData] = React.useState<any>(null);
  const [medicalCardData, setMedicalCardData] = React.useState<any>(null);
  const [lastCompletedStep, setLastCompletedStep] = React.useState<number>(0);
  const hasNavigatedToConfirmation = React.useRef<boolean>(false);

  // Get token from URL query parameter
  const getToken = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token') || '';
  };

  const token = getToken();

  useEffect(() => {
    const fetchMemberData = async () => {
      if (!token) {
        return;
      }

      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;

        const response = await axios.get(
          `${baseURL}/api/v1/members/?load_strategy=with_doctor_and_nutritionist`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setMemberData(response.data);
      } catch (error: any) {
        if (onError) {
          const errorInfo = categorizeError(error);
          onError(errorInfo);
        }
      }
    };

    fetchMemberData();
  }, [token, onError]);

  useEffect(() => {
    const fetchMedicalCardData = async () => {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;

        const response = await axios.get(
          `${baseURL}/api/v1/members/medical-card/${memberData?.id_}/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setMedicalCardData(response.data);
      } catch (error: any) {
        if (onError) {
          const errorInfo = categorizeError(error);
          onError(errorInfo);
        }
      }
    };

    if (token && memberData?.id_) {
      fetchMedicalCardData();
    }
  }, [token, memberData?.id_, memberData, onError]);

  const refreshMedicalCardData = React.useCallback(async () => {
    try {
      const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;
      
      const response = await axios.get(
        `${baseURL}/api/v1/members/medical-card/${memberData?.id_}/`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setMedicalCardData(response.data);
    } catch (error: any) {
      if (onError) {
        const errorInfo = categorizeError(error);
        onError(errorInfo);
      }
    }
  }, [memberData?.id_, token, onError]);

  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  useEffect(() => {
    // Early exit if we're on confirmation page - don't run any logic
    if (location.pathname === '/confirmation') {
      return;
    }

    if (medicalCardData && memberData && !hasInitialized) {
      const scanInfo = medicalCardData.scan_info || {};
      const { number: nextStep, name: stepName } = getOnboardingResumeStep(
        medicalCardData,
        memberData?.membership_type
      );
      
      const declarationComplete = !!scanInfo.onboarding_declaration_confirmed;

      window.dataLayer.push({
        event: `profile_step_${stepName}_started`,
        step_name: stepName,
        step_number: nextStep,
        membership_type: memberData?.membership_type,
        member_id: memberData?.unique_id,
        form_kind: 'onboarding_details',
        product_type: getProductType(window.location.pathname, memberData?.membership_type),
        page_type: 'onboarding',
        amount: localStorage.getItem('subscriptionAmount') || null
      });
      
      if (declarationComplete) {
        
        if (hasNavigatedToConfirmation.current || location.pathname === '/confirmation' || sessionStorage.getItem('onConfirmationPage') === 'true') {
          return; // Exit early to prevent redirect
        }
        
        // Check if membership_type is "scan" (case-insensitive)
        const membershipType = memberData?.membership_type?.toLowerCase();
        const isScanMembership = membershipType === 'scan';
        
        if (isScanMembership) {
          // For scan membership, redirect to confirmation page instead of book-test
          hasNavigatedToConfirmation.current = true;
          navigate(`/confirmation?token=${token}`, { replace: true });
        } else {
          // For other membership types, redirect to booking page
        navigate(`/book-test?token=${token}`, { replace: true });
        }
        return; // Exit early to prevent further execution
      }
      
      if (currentStep.number !== nextStep) {
        setCurrentStep({ number: nextStep, name: stepName });
      }
      
      setHasInitialized(true);
    }
  }, [medicalCardData, memberData, currentStep.number, setCurrentStep, hasInitialized, token, navigate, location.pathname]);

  const saveStepData = React.useCallback(async (values: any, stepNumber: number) => {
    
    try {
      let requestMedicalCardData: any = medicalCardData ? { ...medicalCardData } : {
        basics: {},
        health_lifestyle: {},
        nutritionist_questions: {},
        family_history: {
          family_history_details: null
        },
        goals: {
          goals: [],
          health_concerns: [],
          last_blood_test: null,
          additional_info: null
        },
        female_sex_hormones: null
      };

      delete requestMedicalCardData.created_at;
      delete requestMedicalCardData.updated_at;
      delete requestMedicalCardData.id;

      if (!requestMedicalCardData.goals || typeof requestMedicalCardData.goals !== 'object') {
        requestMedicalCardData.goals = {
          goals: [],
          health_concerns: [],
          last_blood_test: null,
          additional_info: null
        };
      }
      
      if (!requestMedicalCardData.family_history || typeof requestMedicalCardData.family_history !== 'object') {
        requestMedicalCardData.family_history = {
          family_history_details: null
        };
      }

      if (medicalCardData?.basics) {
        requestMedicalCardData.basics = {
          birth_date: medicalCardData.basics.birth_date,
          first_name: medicalCardData.basics.first_name,
          last_name: medicalCardData.basics.last_name,
          email: medicalCardData.basics.email,
          phone: medicalCardData.basics.phone,
          weight: medicalCardData.basics.weight,
          weight_unit: medicalCardData.basics.weight_unit,
          height: medicalCardData.basics.height,
          height_unit: 'cm',
          biological_sex: medicalCardData.basics.biological_sex,
          race_ethnicity: medicalCardData.basics.race_ethnicity,
          address: {
            full_address: medicalCardData.basics.address?.full_address || '',
            address_line_1: medicalCardData.basics.address?.address_line_1 || '',
            country: medicalCardData.basics.address?.country || '',
            postcode: medicalCardData.basics.address?.postcode || '',
            city: medicalCardData.basics.address?.city || ''
          }
        };
      } else {
        requestMedicalCardData.basics = {
          birth_date: null,
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          weight: null,
          weight_unit: 'kg',
          height: null,
          height_unit: 'cm',
          biological_sex: values.gender?.key || null,
          race_ethnicity: values.race?.key || null,
          address: {
            full_address: '',
            address_line_1: '',
            country: '',
            postcode: '',
            city: ''
          }
        };
      }

      if (medicalCardData?.health_lifestyle) {
        requestMedicalCardData.health_lifestyle = {
          activity_level: medicalCardData.health_lifestyle.activity_level,
          overall_health: medicalCardData.health_lifestyle.overall_health,
          medications: medicalCardData.health_lifestyle.medications,
          supplements: normalizeSupplementsFromApi(medicalCardData.health_lifestyle.supplements),
          alcohol_frequency: medicalCardData.health_lifestyle.alcohol_frequency,
          allergies: medicalCardData.health_lifestyle.allergies,
          exercise_frequency: medicalCardData.health_lifestyle.exercise_frequency,
          medical_conditions: medicalCardData.health_lifestyle.medical_conditions,
          previous_surgeries: medicalCardData.health_lifestyle.previous_surgeries,
          smoking_status: medicalCardData.health_lifestyle.smoking_status,
          pregnancy: medicalCardData.health_lifestyle.pregnancy,
          any_specific: medicalCardData.health_lifestyle.any_specific
        };
      }

      if (medicalCardData?.nutritionist_questions) {
        requestMedicalCardData.nutritionist_questions = {
          working_style: medicalCardData.nutritionist_questions.working_style,
          other_working_style: medicalCardData.nutritionist_questions.other_working_style,
          diet_types: medicalCardData.nutritionist_questions.diet_types,
          other_diet_type: medicalCardData.nutritionist_questions.other_diet_type,
          eating_pattern: medicalCardData.nutritionist_questions.eating_pattern,
          other_eating_pattern: medicalCardData.nutritionist_questions.other_eating_pattern,
          hydration: medicalCardData.nutritionist_questions.hydration,
          other_hydration: medicalCardData.nutritionist_questions.other_hydration,
          exercise_routine: medicalCardData.nutritionist_questions.exercise_routine,
          other_exercise_routine: medicalCardData.nutritionist_questions.other_exercise_routine,
          calming_activities: medicalCardData.nutritionist_questions.calming_activities,
          other_calming_activity: medicalCardData.nutritionist_questions.other_calming_activity
        };
      }

      if (medicalCardData?.female_sex_hormones) {
        requestMedicalCardData.female_sex_hormones = {
          last_period_date: medicalCardData.female_sex_hormones.last_period_date,
          hormonal_contraception_hrt: medicalCardData.female_sex_hormones.hormonal_contraception_hrt,
          typical_cycle_length: medicalCardData.female_sex_hormones.typical_cycle_length,
          primary_testing_reason: medicalCardData.female_sex_hormones.primary_testing_reason,
          menstrual_flow_characteristics: medicalCardData.female_sex_hormones.menstrual_flow_characteristics,
          menopause_details: medicalCardData.female_sex_hormones.menopause_details,
          additional_notes_for_clinical_team: medicalCardData.female_sex_hormones.additional_notes_for_clinical_team
        };
      }

      if (medicalCardData?.goals) {
        requestMedicalCardData.goals = {
          goals: medicalCardData.goals.goals || [],
          health_concerns: medicalCardData.goals.health_concerns || [],
          health_concern_details: medicalCardData.goals.health_concern_details || null,
          last_blood_test: medicalCardData.goals.last_blood_test,
          additional_info: medicalCardData.goals.additional_info
        };
      }

      if (medicalCardData?.family_history) {
        requestMedicalCardData.family_history = {
          family_history_details: medicalCardData.family_history.family_history_details
        };
      }

      if (medicalCardData?.scan_info) {
        requestMedicalCardData.scan_info = { ...medicalCardData.scan_info };
      }

      if (values.birthDate || values.first_name || values.last_name || values.email || values.phone || values.weight || values.height || values.gender?.key || values.race?.key || values.full_address) {
        requestMedicalCardData.basics = {
          ...requestMedicalCardData.basics,
          birth_date: values.birthDate ? formatInTimeZone(values.birthDate, Intl.DateTimeFormat().resolvedOptions().timeZone, 'yyyy-MM-dd') : requestMedicalCardData.basics.birth_date,
          first_name: values.first_name || requestMedicalCardData.basics.first_name,
          last_name: values.last_name || requestMedicalCardData.basics.last_name,
          email: memberData?.email || requestMedicalCardData.basics.email,
          phone: memberData?.phone || requestMedicalCardData.basics.phone,
          weight: values.weight ? +values.weight : requestMedicalCardData.basics.weight,
          weight_unit: values.weightUnit?.key || requestMedicalCardData.basics.weight_unit,
          height: values.height ? +values.height : requestMedicalCardData.basics.height,
          height_unit: 'cm',
          biological_sex: values.gender?.key || requestMedicalCardData.basics.biological_sex,
          race_ethnicity: values.race?.key || requestMedicalCardData.basics.race_ethnicity,
          address: {
            full_address: values.full_address || requestMedicalCardData.basics.address?.full_address || '',
            address_line_1: values.address_line_1 || requestMedicalCardData.basics.address?.address_line_1 || '',
            country: values.country || requestMedicalCardData.basics.address?.country || '',
            postcode: values.postalCode || requestMedicalCardData.basics.address?.postcode || '',
            city: values.city || requestMedicalCardData.basics.address?.city || ''
          }
        };
      }

      if (values.activity_level?.key || values.overall_health?.key || values.alcohol_frequency?.key || values.has_allergies || values.exercise_frequency?.key || values.has_medical_conditions || values.has_surgeries || values.has_medications_supplements || values.smoking_status?.key || values.pregnancy !== undefined || values.any_specific || values.supplements?.trim()) {
        const resolvedMedicalConditions =
          values.has_medical_conditions === 'yes'
            ? values.medical_conditions_list || ''
            : values.has_medical_conditions === 'no'
              ? ''
              : requestMedicalCardData.health_lifestyle?.medical_conditions;
        const resolvedSurgeries =
          values.has_surgeries === 'yes'
            ? values.surgeries_details || ''
            : values.has_surgeries === 'no'
              ? ''
              : requestMedicalCardData.health_lifestyle?.previous_surgeries;
        const resolvedSupplements =
          values.has_medications_supplements === 'yes'
            ? (values.supplements ?? '').trim()
            : values.has_medications_supplements === 'no'
              ? ''
              : requestMedicalCardData.health_lifestyle?.supplements;
        const resolvedAllergies =
          values.has_allergies === 'yes'
            ? values.allergies || ''
            : values.has_allergies === 'no'
              ? ''
              : requestMedicalCardData.health_lifestyle?.allergies;

        requestMedicalCardData.health_lifestyle = {
          ...requestMedicalCardData.health_lifestyle,
          activity_level: values.activity_level?.key || requestMedicalCardData.health_lifestyle?.activity_level,
          overall_health: values.overall_health?.key || requestMedicalCardData.health_lifestyle?.overall_health,
          supplements: resolvedSupplements,
          alcohol_frequency: values.alcohol_frequency?.key || requestMedicalCardData.health_lifestyle?.alcohol_frequency,
          allergies: resolvedAllergies,
          exercise_frequency: values.exercise_frequency?.key || requestMedicalCardData.health_lifestyle?.exercise_frequency,
          medical_conditions: resolvedMedicalConditions,
          previous_surgeries: resolvedSurgeries,
          smoking_status: values.smoking_status?.key || requestMedicalCardData.health_lifestyle?.smoking_status,
          pregnancy: values.pregnancy !== undefined ? values.pregnancy : requestMedicalCardData.health_lifestyle?.pregnancy,
          any_specific: values.any_specific || requestMedicalCardData.health_lifestyle?.any_specific
        };
      }

      if (values.working_style?.key || values.other_working_style || values.diet_types?.length || values.other_diet_type || values.eating_pattern?.key || values.other_eating_pattern || values.hydration?.key || values.other_hydration || values.exercise_routine?.key || values.other_exercise_routine || values.calming_activities?.length || values.other_calming_activity) {
        requestMedicalCardData.nutritionist_questions = {
          ...requestMedicalCardData.nutritionist_questions,
          working_style: values.working_style?.key || requestMedicalCardData.nutritionist_questions?.working_style,
          other_working_style: values.other_working_style || requestMedicalCardData.nutritionist_questions?.other_working_style,
          diet_types: values.diet_types?.map((diet: { key: any; value: string }) => diet.key) || requestMedicalCardData.nutritionist_questions?.diet_types,
          other_diet_type: values.other_diet_type || requestMedicalCardData.nutritionist_questions?.other_diet_type,
          eating_pattern: values.eating_pattern?.key || requestMedicalCardData.nutritionist_questions?.eating_pattern,
          other_eating_pattern: values.other_eating_pattern || requestMedicalCardData.nutritionist_questions?.other_eating_pattern,
          hydration: values.hydration?.key || requestMedicalCardData.nutritionist_questions?.hydration,
          other_hydration: values.other_hydration || requestMedicalCardData.nutritionist_questions?.other_hydration,
          exercise_routine: values.exercise_routine?.key || requestMedicalCardData.nutritionist_questions?.exercise_routine,
          other_exercise_routine: values.other_exercise_routine || requestMedicalCardData.nutritionist_questions?.other_exercise_routine,
          calming_activities: values.calming_activities?.map((activity: { key: any; value: string }) => activity.key) || requestMedicalCardData.nutritionist_questions?.calming_activities,
          other_calming_activity: values.other_calming_activity || requestMedicalCardData.nutritionist_questions?.other_calming_activity
        };
      } else if (requestMedicalCardData.nutritionist_questions) {
        requestMedicalCardData.nutritionist_questions = {
          ...requestMedicalCardData.nutritionist_questions
        };
      }

      const isFemale = values.gender?.key === 'female';

      if (isFemale && stepNumber === 3) {
        requestMedicalCardData.scan_info = {
          ...(requestMedicalCardData.scan_info || {}),
          onboarding_hormones_step_completed: true
        };
      }

      if (
        isFemale &&
        (stepNumber === 3 ||
          values.last_period_date ||
          values.hormonal_contraception_hrt ||
          values.typical_cycle_length ||
          values.primary_testing_reason ||
          values.menstrual_flow_characteristics ||
          values.menopause_details ||
          values.additional_notes_for_clinical_team)
      ) {
        requestMedicalCardData.female_sex_hormones = {
          ...requestMedicalCardData.female_sex_hormones,
          last_period_date: values.last_period_date
            ? formatInTimeZone(values.last_period_date, Intl.DateTimeFormat().resolvedOptions().timeZone, 'yyyy-MM-dd')
            : requestMedicalCardData.female_sex_hormones?.last_period_date ?? null,
          hormonal_contraception_hrt:
            values.hormonal_contraception_hrt ??
            requestMedicalCardData.female_sex_hormones?.hormonal_contraception_hrt ??
            null,
          typical_cycle_length:
            values.typical_cycle_length ??
            requestMedicalCardData.female_sex_hormones?.typical_cycle_length ??
            null,
          primary_testing_reason:
            values.primary_testing_reason ??
            requestMedicalCardData.female_sex_hormones?.primary_testing_reason ??
            null,
          menstrual_flow_characteristics:
            values.menstrual_flow_characteristics ??
            requestMedicalCardData.female_sex_hormones?.menstrual_flow_characteristics ??
            null,
          menopause_details:
            values.menopause_details ??
            requestMedicalCardData.female_sex_hormones?.menopause_details ??
            null,
          additional_notes_for_clinical_team:
            values.additional_notes_for_clinical_team ??
            requestMedicalCardData.female_sex_hormones?.additional_notes_for_clinical_team ??
            null
        };
      }

      if (values.goals || values.health_concerns || values.health_concern_details || values.last_blood_test || values.additional_info) {
        requestMedicalCardData.goals = {
          ...requestMedicalCardData.goals,
          goals: values.goals?.map((goal: any) => goal.key) || requestMedicalCardData.goals?.goals || [],
          health_concerns: values.health_concerns?.map((concern: any) => concern.key) || requestMedicalCardData.goals?.health_concerns || [],
          health_concern_details: values.health_concern_details || requestMedicalCardData.goals?.health_concern_details || null,
          last_blood_test: values.last_blood_test ? formatInTimeZone(values.last_blood_test, Intl.DateTimeFormat().resolvedOptions().timeZone, 'yyyy-MM-dd') : requestMedicalCardData.goals?.last_blood_test,
          additional_info: values.additional_info || requestMedicalCardData.goals?.additional_info
        };
      }

      if (values.family_history_details) {
        requestMedicalCardData.family_history = {
          ...requestMedicalCardData.family_history,
          family_history_details: values.family_history_details || requestMedicalCardData.family_history?.family_history_details
        };
      }

      if (
        values.reason_for_scan ||
        values.current_symptoms ||
        values.has_cancer_history ||
        values.previous_imaging_details ||
        values.scan_family_history_details ||
        values.medical_implants?.length ||
        values.practical_considerations?.length ||
        Object.keys(values.disclaimer_confirmations || {}).length
      ) {
        requestMedicalCardData.scan_info = {
          ...(requestMedicalCardData.scan_info || {}),
          reason_for_scan: values.reason_for_scan || requestMedicalCardData.scan_info?.reason_for_scan || null,
          current_symptoms: values.current_symptoms || requestMedicalCardData.scan_info?.current_symptoms || null,
          cancer_history: values.has_cancer_history === 'yes' ? 'Yes' : values.has_cancer_history === 'no' ? 'No' : requestMedicalCardData.scan_info?.cancer_history || null,
          cancer_history_details:
            values.has_cancer_history === 'yes'
              ? values.cancer_history_details || ''
              : values.has_cancer_history === 'no'
                ? ''
                : requestMedicalCardData.scan_info?.cancer_history_details || null,
          any_previous_imaging: values.previous_imaging_details || requestMedicalCardData.scan_info?.any_previous_imaging || null,
          scan_family_history_details: values.scan_family_history_details || requestMedicalCardData.scan_info?.scan_family_history_details || null,
          medical_implants: values.medical_implants?.map((item: any) => item.key) || requestMedicalCardData.scan_info?.medical_implants || [],
          metallic_implants_details: values.medical_implants_details || requestMedicalCardData.scan_info?.metallic_implants_details || null,
          practical_considerations: values.practical_considerations?.map((item: any) => item.key) || requestMedicalCardData.scan_info?.practical_considerations || [],
          practical_considerations_details: values.practical_considerations_details || requestMedicalCardData.scan_info?.practical_considerations_details || null,
          disclaimer_confirmations: values.disclaimer_confirmations || requestMedicalCardData.scan_info?.disclaimer_confirmations || {},
        };
      }

      // Ensure family_history always present for schema
      if (!requestMedicalCardData.family_history) {
        requestMedicalCardData.family_history = { family_history_details: null };
      }

      // Keep profile basics current on every step save (backend syncs these to members table).
      if (!requestMedicalCardData.basics) {
        requestMedicalCardData.basics = {};
      }
      if (values.first_name) {
        requestMedicalCardData.basics.first_name = values.first_name;
      }
      if (values.last_name) {
        requestMedicalCardData.basics.last_name = values.last_name;
      }
      if (memberData?.email) {
        requestMedicalCardData.basics.email = memberData.email;
      }
      if (memberData?.phone) {
        requestMedicalCardData.basics.phone = memberData.phone;
      }
      if (values.birthDate) {
        requestMedicalCardData.basics.birth_date = formatInTimeZone(
          values.birthDate,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
          'yyyy-MM-dd'
        );
      }
      if (values.gender?.key) {
        requestMedicalCardData.basics.biological_sex = values.gender.key;
      }

      const finalStep = getOnboardingTotalSteps(
        memberData?.membership_type,
        values.gender?.key === 'female'
      );

      const requestObj = {
        token: token,
        medical_card: requestMedicalCardData,
        onboarding_form_completed: stepNumber === finalStep,
      };

      const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

      const response = await axios.post(
        `${baseURL}/api/v1/members/complete-profile`,
        requestObj,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      setLastCompletedStep(stepNumber);
      
      await refreshMedicalCardData();
      
      // Don't redirect if we're already on confirmation page
      if (location.pathname === '/confirmation' || hasNavigatedToConfirmation.current || sessionStorage.getItem('onConfirmationPage') === 'true') {
        return;
      }
      
      // Check if membership_type is "scan" (case-insensitive)
      const membershipType = memberData?.membership_type?.toLowerCase();
      const isScanMembership = membershipType === 'scan';
      
      if (stepNumber === finalStep) {
        if (isScanMembership) {
          // Redirect to confirmation page for scan membership
          hasNavigatedToConfirmation.current = true;
          navigate(`/confirmation?token=${token}`, { replace: true });
        } else if (response.data?.booking_token) {
        // Navigate to booking page with the booking token
        navigate(`/book-test?token=${response.data.booking_token}`, { replace: true });
        } else {
        // If no booking token but it's the final step, still navigate with original token
        navigate(`/book-test?token=${token}`, { replace: true });
        }
      }
    } catch (error: any) {
      if (onError) {
        const errorInfo = categorizeError(error);
        onError(errorInfo);
      }
      throw error;
    }
  }, [medicalCardData, memberData, token, navigate, refreshMedicalCardData, location.pathname, onError]);

  // Function to populate form with saved data
  const populateFormWithSavedData = React.useCallback((values: any, setFieldValue: any) => {
    if (!medicalCardData) return;

    const convertUTCToLocalDate = (dateString: string | null) => {
      if (!dateString) return null;
      try {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
            } catch (error) {
        return null;
      }
    };

    const mapEnumToDisplayText = (enumValue: string, options: any[]) => {
      const option = options.find(opt => opt.key === enumValue);
      return option ? option.value : enumValue;
    };

    if (medicalCardData.basics) {
      const basics = medicalCardData.basics;
      if (basics.birth_date) setFieldValue('birthDate', convertUTCToLocalDate(basics.birth_date));
      if (basics.first_name && (!values.first_name || values.first_name === '')) setFieldValue('first_name', basics.first_name);
      if (basics.last_name && (!values.last_name || values.last_name === '')) setFieldValue('last_name', basics.last_name);
      if (basics.email && !values.email) setFieldValue('email', basics.email);
      if (basics.phone && !values.phone) setFieldValue('phone', basics.phone);
      if (basics.weight) setFieldValue('weight', basics.weight.toString());
      if (basics.height) setFieldValue('height', basics.height.toString());
      if (basics.biological_sex) {
        setFieldValue('gender', {
          key: basics.biological_sex,
          value: mapEnumToDisplayText(basics.biological_sex, genderDisplayOptions),
        });
      }
      if (basics.race_ethnicity) {
        setFieldValue('race', {
          key: basics.race_ethnicity,
          value: mapEnumToDisplayText(basics.race_ethnicity, races),
        });
      }
      if (basics.weight_unit) {
        setFieldValue('weightUnit', {
          key: basics.weight_unit,
          value: mapEnumToDisplayText(basics.weight_unit, weightMeasurementTypes),
        });
      }
      setFieldValue('heightUnit', { key: 'cm', value: 'cm' });
      if (basics.address) {
        setFieldValue('full_address', basics.address.full_address || '');
        setFieldValue('address_line_1', basics.address.address_line_1 || '');
        setFieldValue('country', basics.address.country || '');
        setFieldValue('postalCode', basics.address.postcode || '');
        setFieldValue('city', basics.address.city || '');
      }
    }

    if (medicalCardData.health_lifestyle) {
      const health = medicalCardData.health_lifestyle;
      if (health.activity_level) setFieldValue('activity_level', { key: health.activity_level, value: mapEnumToDisplayText(health.activity_level, activityLevelOptions) });
      if (health.overall_health) setFieldValue('overall_health', { key: health.overall_health, value: mapEnumToDisplayText(health.overall_health, overallHealthOptions) });
      if (health.alcohol_frequency) setFieldValue('alcohol_frequency', { key: health.alcohol_frequency, value: mapEnumToDisplayText(health.alcohol_frequency, alcoholFrequencyOptions) });
      if (health.exercise_frequency) setFieldValue('exercise_frequency', { key: health.exercise_frequency, value: mapEnumToDisplayText(health.exercise_frequency, exerciseFrequencyOptions) });
      if (health.smoking_status) setFieldValue('smoking_status', { key: health.smoking_status, value: mapEnumToDisplayText(health.smoking_status, smokingStatusOptions) });
      if (health.pregnancy !== undefined) setFieldValue('pregnancy', health.pregnancy);
      if (health.any_specific) setFieldValue('any_specific', health.any_specific);

      const setYesNoFromStored = (
        answerField: string,
        detailField: string,
        stored: string | null | undefined
      ) => {
        if (stored === undefined || stored === null) return;
        if (String(stored).trim()) {
          setFieldValue(answerField, 'yes');
          setFieldValue(detailField, stored);
        } else {
          setFieldValue(answerField, 'no');
          setFieldValue(detailField, '');
        }
      };

      setYesNoFromStored('has_medical_conditions', 'medical_conditions_list', health.medical_conditions);
      setYesNoFromStored('has_surgeries', 'surgeries_details', health.previous_surgeries);
      setYesNoFromStored(
        'has_medications_supplements',
        'supplements',
        health.supplements == null ? null : normalizeSupplementsFromApi(health.supplements)
      );
      setYesNoFromStored('has_allergies', 'allergies', health.allergies);
    }

    if (medicalCardData.nutritionist_questions) {
      const nutritionist = medicalCardData.nutritionist_questions;
      if (nutritionist.working_style) setFieldValue('working_style', { key: nutritionist.working_style, value: mapEnumToDisplayText(nutritionist.working_style, workingStyleOptions) });
      if (nutritionist.other_working_style) setFieldValue('other_working_style', nutritionist.other_working_style);
      if (nutritionist.diet_types && nutritionist.diet_types.length > 0) {
        const dietTypesArray = nutritionist.diet_types.map((diet: string) => ({ key: diet, value: mapEnumToDisplayText(diet, dietTypeOptions) }));
        setFieldValue('diet_types', dietTypesArray);
      }
      if (nutritionist.other_diet_type) setFieldValue('other_diet_type', nutritionist.other_diet_type);
      if (nutritionist.eating_pattern) setFieldValue('eating_pattern', { key: nutritionist.eating_pattern, value: mapEnumToDisplayText(nutritionist.eating_pattern, eatingPatternOptions) });
      if (nutritionist.other_eating_pattern) setFieldValue('other_eating_pattern', nutritionist.other_eating_pattern);
      if (nutritionist.hydration) setFieldValue('hydration', { key: nutritionist.hydration, value: mapEnumToDisplayText(nutritionist.hydration, hydrationOptions) });
      if (nutritionist.other_hydration) setFieldValue('other_hydration', nutritionist.other_hydration);
      if (nutritionist.exercise_routine) setFieldValue('exercise_routine', { key: nutritionist.exercise_routine, value: mapEnumToDisplayText(nutritionist.exercise_routine, exerciseRoutineOptions) });
      if (nutritionist.other_exercise_routine) setFieldValue('other_exercise_routine', nutritionist.other_exercise_routine);
      if (nutritionist.calming_activities && nutritionist.calming_activities.length > 0) {
        const calmingActivitiesArray = nutritionist.calming_activities.map((activity: string) => ({ key: activity, value: mapEnumToDisplayText(activity, calmingActivityOptions) }));
        setFieldValue('calming_activities', calmingActivitiesArray);
      }
      if (nutritionist.other_calming_activity) setFieldValue('other_calming_activity', nutritionist.other_calming_activity);
    }

    if (medicalCardData.female_sex_hormones) {
      const hormones = medicalCardData.female_sex_hormones;
      if (hormones.last_period_date) setFieldValue('last_period_date', convertUTCToLocalDate(hormones.last_period_date));
      if (hormones.hormonal_contraception_hrt) setFieldValue('hormonal_contraception_hrt', hormones.hormonal_contraception_hrt);
      if (hormones.typical_cycle_length) setFieldValue('typical_cycle_length', hormones.typical_cycle_length);
      if (hormones.primary_testing_reason) setFieldValue('primary_testing_reason', hormones.primary_testing_reason);
      if (hormones.menstrual_flow_characteristics) setFieldValue('menstrual_flow_characteristics', hormones.menstrual_flow_characteristics);
      if (hormones.menopause_details) setFieldValue('menopause_details', hormones.menopause_details);
      if (hormones.additional_notes_for_clinical_team) setFieldValue('additional_notes_for_clinical_team', hormones.additional_notes_for_clinical_team);
    }

    if (medicalCardData.goals) {
      const goals = medicalCardData.goals;
      if (goals.goals && goals.goals.length > 0) {
        const goalsArray = goals.goals.map((goal: string) => ({ key: goal, value: goal }));
        setFieldValue('goals', goalsArray);
      }
      if (goals.health_concerns && goals.health_concerns.length > 0) {
        const concernsArray = goals.health_concerns.map((concern: string) => ({ key: concern, value: concern }));
        setFieldValue('health_concerns', concernsArray);
      }
      if (goals.health_concern_details) setFieldValue('health_concern_details', goals.health_concern_details);
      if (goals.last_blood_test) setFieldValue('last_blood_test', convertUTCToLocalDate(goals.last_blood_test));
      if (goals.additional_info) setFieldValue('additional_info', goals.additional_info);
    }

    if (medicalCardData.family_history) {
      const family = medicalCardData.family_history;
      if (family.family_history_details) setFieldValue('family_history_details', family.family_history_details);
    }

    if (medicalCardData.scan_info) {
      const scan = medicalCardData.scan_info;
      if (scan.reason_for_scan) setFieldValue('reason_for_scan', scan.reason_for_scan);
      if (scan.current_symptoms) setFieldValue('current_symptoms', scan.current_symptoms);
      if (scan.cancer_history === 'Yes') {
        setFieldValue('has_cancer_history', 'yes');
        setFieldValue('cancer_history_details', scan.cancer_history_details || '');
      } else if (scan.cancer_history === 'No') {
        setFieldValue('has_cancer_history', 'no');
        setFieldValue('cancer_history_details', '');
      }
      if (scan.any_previous_imaging) setFieldValue('previous_imaging_details', scan.any_previous_imaging);
      if (scan.scan_family_history_details) setFieldValue('scan_family_history_details', scan.scan_family_history_details);
      if (Array.isArray(scan.medical_implants) && scan.medical_implants.length) {
        setFieldValue(
          'medical_implants',
          scan.medical_implants.map((item: string) => ({
            key: item,
            value: mapEnumToDisplayText(item, medicalImplantOptions)
          }))
        );
      }
      if (scan.metallic_implants_details) setFieldValue('medical_implants_details', scan.metallic_implants_details);
      if (Array.isArray(scan.practical_considerations) && scan.practical_considerations.length) {
        setFieldValue(
          'practical_considerations',
          scan.practical_considerations.map((item: string) => ({
            key: item,
            value: mapEnumToDisplayText(item, practicalConsiderationOptions)
          }))
        );
      }
      if (scan.practical_considerations_details) {
        setFieldValue('practical_considerations_details', scan.practical_considerations_details);
      }
      if (scan.disclaimer_confirmations && typeof scan.disclaimer_confirmations === 'object') {
        setFieldValue('disclaimer_confirmations', scan.disclaimer_confirmations);
      }
    }
  }, [medicalCardData]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => {
        setIsLoading(true);
        setIsFormError(false);

        const requestObj = {
          token: token,
          onboarding_form_completed: true,
          medical_card: {
            basics: {
              birth_date: formatInTimeZone(
                values.birthDate!,
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                'yyyy-MM-dd'
              ),
              first_name: values.first_name,
              last_name: values.last_name,
              email: memberData?.email || values.email,
              phone: memberData?.phone || values.phone,
              weight: +values.weight,
              weight_unit: values.weightUnit.key,
              height: +values.height,
              height_unit: 'cm',
              biological_sex: values.gender.key,
              race_ethnicity: values.race.key,
              address: {
                full_address: values.full_address,
                address_line_1: values.address_line_1,
                country: values.country,
                postcode: values.postalCode,
                city: values.city
              }
            },
            health_lifestyle: {
              activity_level: values.activity_level.key,
              overall_health: values.overall_health.key,
              medications: medicalCardData?.health_lifestyle?.medications ?? null,
              supplements:
                values.has_medications_supplements === 'yes'
                  ? (values.supplements ?? '').trim()
                  : values.has_medications_supplements === 'no'
                    ? ''
                    : (values.supplements ?? '').trim() || null,
              alcohol_frequency: values.alcohol_frequency.key,
              allergies:
                values.has_allergies === 'yes'
                  ? values.allergies || ''
                  : values.has_allergies === 'no'
                    ? ''
                    : values.allergies || null,
              exercise_frequency: values.exercise_frequency?.key || null,
              medical_conditions:
                values.has_medical_conditions === 'yes'
                  ? values.medical_conditions_list || ''
                  : values.has_medical_conditions === 'no'
                    ? ''
                    : values.medical_conditions_list || null,
              previous_surgeries:
                values.has_surgeries === 'yes'
                  ? values.surgeries_details || ''
                  : values.has_surgeries === 'no'
                    ? ''
                    : values.surgeries_details || null,
              smoking_status: values.smoking_status.key,
              pregnancy: values.pregnancy,
              any_specific: values.any_specific || null
            },
            nutritionist_questions: {
              working_style: values.working_style?.key || null,
              other_working_style: values.other_working_style || null,
              diet_types: values.diet_types?.map((diet: { key: any; value: string }) => diet.key) || [],
              other_diet_type: values.other_diet_type || null,
              eating_pattern: values.eating_pattern?.key || null,
              other_eating_pattern: values.other_eating_pattern || null,
              hydration: values.hydration?.key || null,
              other_hydration: values.other_hydration || null,
              exercise_routine: values.exercise_routine?.key || null,
              other_exercise_routine: values.other_exercise_routine || null,
              calming_activities: values.calming_activities?.map((activity: { key: any; value: string }) => activity.key) || [],
              other_calming_activity: values.other_calming_activity || null
            },
            family_history: {
              family_history_details: values.family_history_details || null
            },
            goals: {
              goals: values.goals?.map((goal) => goal.key) || [],
              health_concerns: values.health_concerns?.map(
                (concern) => concern.key
              ) || [],
              health_concern_details: values.health_concern_details || null,
              last_blood_test: values.last_blood_test ? formatInTimeZone(
                values.last_blood_test,
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                'yyyy-MM-dd'
              ) : null,
              additional_info: values.additional_info || null
            },
            female_sex_hormones: values.gender.key === 'female'
              ? {
                  last_period_date: values.last_period_date
                    ? formatInTimeZone(
                        values.last_period_date,
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                        'yyyy-MM-dd'
                      )
                    : null,
                  hormonal_contraception_hrt: values.hormonal_contraception_hrt,
                  typical_cycle_length: values.typical_cycle_length,
                  primary_testing_reason: values.primary_testing_reason,
                  menstrual_flow_characteristics:
                    values.menstrual_flow_characteristics,
                  menopause_details: values.menopause_details,
                  additional_notes_for_clinical_team:
                    values.additional_notes_for_clinical_team
                }
              : medicalCardData?.female_sex_hormones ?? null,
            scan_info: {
              ...(medicalCardData?.scan_info || {}),
              reason_for_scan: values.reason_for_scan || null,
              current_symptoms: values.current_symptoms || null,
              cancer_history:
                values.has_cancer_history === 'yes'
                  ? 'Yes'
                  : values.has_cancer_history === 'no'
                    ? 'No'
                    : null,
              cancer_history_details:
                values.has_cancer_history === 'yes'
                  ? values.cancer_history_details || ''
                  : '',
              any_previous_imaging: values.previous_imaging_details || null,
              scan_family_history_details: values.scan_family_history_details || null,
              medical_implants: values.medical_implants?.map((item) => item.key) || [],
              metallic_implants_details: values.medical_implants_details || null,
              practical_considerations:
                values.practical_considerations?.map((item) => item.key) || [],
              practical_considerations_details:
                values.practical_considerations_details || null,
              disclaimer_confirmations: values.disclaimer_confirmations || {},
            }
          }
        };
        
        try {
          const baseURL = isProd
            ? process.env.REACT_APP_API_BASE_URL_PROD
            : process.env.REACT_APP_API_BASE_URL_DEV;

          const response = await axios.post(
            `${baseURL}/api/v1/members/complete-profile`,
            requestObj,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
          
          setIsSubmitted(true);
          setIsLoading(false);
          
          await refreshMedicalCardData();
          
          // Don't redirect if we're already on confirmation page or have already navigated there
          if (location.pathname === '/confirmation' || hasNavigatedToConfirmation.current || sessionStorage.getItem('onConfirmationPage') === 'true') {
            return;
          }
          
          // Check if membership_type is "scan" (case-insensitive)
          const membershipType = memberData?.membership_type?.toLowerCase();
          const isScanMembership = membershipType === 'scan';
          
          if (isScanMembership) {
            // Redirect to confirmation page for scan membership
            hasNavigatedToConfirmation.current = true;
            navigate(`/confirmation?token=${token}`, { replace: true });
          } else {
          // Extract booking_token from response and use it for navigation
          const bookingToken = response.data?.booking_token || token;
          navigate(`/book-test?token=${bookingToken}`, { replace: true });
          }
        } catch (error) {
          setIsFormError(true);
          if (onError) {
            const errorInfo = categorizeError(error);
            onError(errorInfo);
          }
        } finally {
          setIsLoading(false);
        }
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        const membershipType = memberData?.membership_type;
        const isScanOrPlus = isScanOrPlusMembership(membershipType);
        const isFemale = values.gender?.key === 'female';
        const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);
        const totalStepsDisplay = getOnboardingTotalSteps(membershipType, isFemale);

        // Female hormones at step 3. MRI only for Scan / Plus. Declaration final for all.
        let stepComponents: Record<number, React.ComponentType<any>>;
        if (hasHormonesStep) {
          stepComponents = isScanOrPlus
            ? {
                1: Step1,
                2: Step2,
                3: Step5,
                4: MedicalHistoryStep,
                5: MRISafetyStep,
                6: Step4,
                7: DeclarationStep
              }
            : {
                1: Step1,
                2: Step2,
                3: Step5,
                4: MedicalHistoryStep,
                5: Step4,
                6: DeclarationStep
              };
        } else if (isScanOrPlus) {
          stepComponents = {
            1: Step1,
            2: Step2,
            3: MedicalHistoryStep,
            4: MRISafetyStep,
            5: Step4,
            6: DeclarationStep
          };
        } else {
          stepComponents = {
            1: Step1,
            2: Step2,
            3: MedicalHistoryStep,
            4: Step4,
            5: DeclarationStep
          };
        }

        const showIncompleteReminder = shouldShowIncompleteStepReminder(
          currentStep.number,
          values,
          membershipType
        );

        const StepComponent = stepComponents[currentStep.number] || Step1;
        const finalStep = totalStepsDisplay;
        const stepProps = {
          setCurrentStep,
          memberData,
          membershipType,
          saveStepData: async (stepNumber: number) => {
            const isFinalStep = stepNumber === totalStepsDisplay;
            if (isFinalStep) {
              setIsLoading(true);
            }
            try {
              await saveStepData(values, stepNumber);
            } finally {
              if (isFinalStep) {
                setIsLoading(false);
              }
            }
          },
          populateFormWithSavedData: () => populateFormWithSavedData(values, setFieldValue),
          lastCompletedStep,
          ...(currentStep.number === finalStep && { isLoading })
        };

        return (
          <>
            <div className='header-container'>
              <div className='form-header'>Complete Profile</div>
              <div className='desc'>
                Please follow these {totalStepsDisplay} simple steps to get started!
              </div>
              {showIncompleteReminder && (
                <div className='incomplete-step-reminder' role='status'>
                  Please fill this in with as many details as possible. Your CoreVitals Doctor and
                  Nutritionist will use this information to review your results
                </div>
              )}
            </div>
            <div className='stepper-wrapper'>
              <div className='stepper'>
                Step {currentStep.number} of {totalStepsDisplay}
              </div>
              <div className='step-name'>{currentStep.name}</div>
            </div>
            <form className='user-form' onSubmit={handleSubmit}>
              <StepComponent {...stepProps} />
            </form>
          </>
        );
      }}
    </Formik>
  );
};
export default CompleteProfileForm;
